'use client';

/**
 * Marketing subscribe card — split layout with decorative form on the
 * left and plan summary on the right. Ported from ThemeMaker's
 * {@code blocks/cta/SubscribeCard}.
 *
 * <p><b>Important:</b> this is a MARKETING component, NOT a real form
 * handler. The CTA is an {@code <a>} styled as a button — clicking
 * navigates to the sign-in/sign-up route. Input fields are decorative.
 * Real subscription flows replace this block with a dedicated form
 * route using server actions (custom validation + auth wiring + error
 * states belong there, not here).
 */
import { useTranslation } from 'react-i18next';
import { Card } from '@akademi/ui';
import { tx } from '../_shared/coerce';
import { sectionInner } from '../_shared/styles';
import { PortalLink } from '@akademi/site-builder-runtime';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

export interface SubscribeCardProps {
  title?: Localized;
  subtitle?: Localized;
  planName?: Localized;
  planPrice?: string;
  planPeriod?: Localized;
  planFeatures?: Localized[];
  ctaLabel?: Localized;
  ctaHref?: string;
  termsText?: Localized;
  emailLabel?: Localized;
  passwordLabel?: Localized;
  showPassword?: boolean;
}

export function SubscribeCard({
  title,
  subtitle,
  planName,
  planPrice,
  planPeriod,
  planFeatures = [],
  ctaLabel,
  ctaHref = '/sign-in',
  termsText,
  emailLabel,
  passwordLabel,
  showPassword = true,
}: SubscribeCardProps) {
  const { t } = useTranslation('site-builder-blocks');
  const headline = title ?? t('subscribe.title');
  const plan = planName ?? t('subscribe.planName');
  const cta = ctaLabel ?? t('subscribe.cta');
  const emailText = emailLabel ?? t('subscribe.emailLabel');
  const passwordText = passwordLabel ?? t('subscribe.passwordLabel');
  return (
    <BlockRoot
      as="section"
      style={{
        padding: 'clamp(40px, 5vw, 80px) var(--space-6, 24px)',
        background: 'var(--color-surface-page, #fbf7ee)',
      }}
    >
      <div style={{ ...sectionInner, maxWidth: 960 }}>
        <style
          dangerouslySetInnerHTML={{
            __html: `
          /* Rendered as <Card>: border + elevation come from the component. */
          .lms-sub-card { display: grid; grid-template-columns: 1fr 360px; gap: 0; background: var(--color-surface-card, #fff); border-radius: var(--radius-md, 4px); overflow: hidden; }
          @media (max-width: 720px) { .lms-sub-card { grid-template-columns: 1fr; } }
          .lms-sub-input { width: 100%; box-sizing: border-box; padding: 14px 16px; border: 1px solid var(--color-border-default, rgba(0,0,0,0.18)); background: var(--color-surface-page, #fbf7ee); border-radius: var(--radius-md, 4px); font-size: 15px; font-family: inherit; color: var(--color-text-primary, #1a1614); transition: border-color 180ms ease, background 180ms ease; }
          .lms-sub-input:focus { outline: none; border-color: var(--color-brand-500, #722f37); background: var(--color-surface-card, #fff); }
          .lms-sub-input::placeholder { color: var(--color-text-tertiary, #94867d); }
        `,
          }}
        />

        <Card className="lms-sub-card">
          {/* Left: form */}
          <div style={{ padding: 'clamp(28px, 3vw, 48px)' }}>
            <div
              style={{
                fontSize: '11px',
                fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--font-tracking-widest, 0.12em)',
                color: 'var(--color-text-tertiary, #94867d)',
                marginBottom: 12,
              }}
            >
              {t('subscribe.step')}
            </div>

            <h2
              style={{
                margin: 0,
                fontSize: 'clamp(28px, 3vw, 40px)',
                lineHeight: 1.1,
                fontWeight: 500,
                fontStyle: 'italic',
                fontFamily: 'var(--font-family-display, "DM Serif Display", Georgia, serif)',
                color: 'var(--color-text-primary, #1a1614)',
                letterSpacing: 'var(--font-tracking-snug, -0.015em)',
              }}
            >
              {tx(headline)}
            </h2>

            {subtitle && (
              <p
                style={{
                  marginTop: 8,
                  fontSize: 'var(--font-size-base, 15px)',
                  lineHeight: 1.55,
                  color: 'var(--color-text-secondary, #5d4f47)',
                }}
              >
                {tx(subtitle)}
              </p>
            )}

            <div
              style={{
                marginTop: 24,
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
              }}
            >
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: 'var(--font-size-xs, 12px)',
                    fontWeight: 600,
                    marginBottom: 6,
                    color: 'var(--color-text-secondary, #5d4f47)',
                    letterSpacing: 'var(--font-tracking-wide, 0.02em)',
                  }}
                >
                  {tx(emailText)}
                </label>
                <input
                  type="email"
                  name="email"
                  className="lms-sub-input"
                  placeholder={t('subscribe.emailPlaceholder')}
                />
              </div>
              {showPassword && (
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 'var(--font-size-xs, 12px)',
                      fontWeight: 600,
                      marginBottom: 6,
                      color: 'var(--color-text-secondary, #5d4f47)',
                      letterSpacing: 'var(--font-tracking-wide, 0.02em)',
                    }}
                  >
                    {tx(passwordText)}
                  </label>
                  <input
                    type="password"
                    name="password"
                    className="lms-sub-input"
                    placeholder="••••••••"
                  />
                </div>
              )}
            </div>

            <PortalLink
              href={ctaHref}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                boxSizing: 'border-box',
                marginTop: 20,
                padding: 'var(--space-3-5, 14px) var(--space-6, 24px)',
                background: 'var(--color-brand-500, #722f37)',
                color: 'var(--color-text-on-brand, #fff)',
                borderRadius: 'var(--radius-pill, 9999px)',
                fontSize: 'var(--font-size-base, 15px)',
                fontWeight: 600,
                textDecoration: 'none',
                letterSpacing: 'var(--font-tracking-wide, 0.02em)',
              }}
            >
              {tx(cta)}
            </PortalLink>

            {termsText && (
              <p
                style={{
                  marginTop: 16,
                  fontSize: 'var(--font-size-xs, 12px)',
                  lineHeight: 1.5,
                  color: 'var(--color-text-tertiary, #94867d)',
                }}
              >
                {tx(termsText)}
              </p>
            )}
          </div>

          {/* Right: plan summary. Different background so the seam
           *  between the two halves reads as a divider without an
           *  explicit border. */}
          <aside
            style={{
              padding: 'clamp(28px, 3vw, 48px)',
              background: 'var(--color-surface-page, #fbf7ee)',
              borderLeft: '1px solid var(--color-border-subtle, rgba(0,0,0,0.06))',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <div
              style={{
                fontSize: '11px',
                fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--font-tracking-widest, 0.12em)',
                color: 'var(--color-text-tertiary, #94867d)',
              }}
            >
              {t('subscribe.yourPlan')}
            </div>

            <div
              style={{
                fontSize: 'clamp(22px, 2vw, 28px)',
                fontWeight: 500,
                fontStyle: 'italic',
                fontFamily: 'var(--font-family-display, "DM Serif Display", Georgia, serif)',
                color: 'var(--color-text-primary, #1a1614)',
                lineHeight: 1.1,
              }}
            >
              {tx(plan)}
            </div>

            {planPrice && (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span
                  style={{
                    fontSize: 'clamp(28px, 3vw, 36px)',
                    fontWeight: 700,
                    fontFamily: 'var(--font-family-display, "DM Serif Display", Georgia, serif)',
                    color: 'var(--color-text-primary, #1a1614)',
                    lineHeight: 1,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {planPrice}
                </span>
                {planPeriod && (
                  <span
                    style={{
                      fontSize: '13px',
                      color: 'var(--color-text-secondary, #5d4f47)',
                    }}
                  >
                    {tx(planPeriod)}
                  </span>
                )}
              </div>
            )}

            {planFeatures.length > 0 && (
              <ul
                style={{
                  margin: 0,
                  padding: 0,
                  listStyle: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  fontSize: '13px',
                  color: 'var(--color-text-secondary, #5d4f47)',
                  paddingTop: 8,
                  borderTop: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
                }}
              >
                {planFeatures.map((f, i) => (
                  <li
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                      lineHeight: 1.45,
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        flexShrink: 0,
                        color: 'var(--color-brand-500, #722f37)',
                        marginTop: 4,
                      }}
                    >
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    <span>{tx(f)}</span>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </Card>
      </div>
    </BlockRoot>
  );
}
